import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { CONTACTS } from "@/constants/testIds";
import { useSectionTheme } from "@/hooks/useSectionTheme";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Search, Plus, ChevronRight, Star } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { key: "all", label: "Everyone" },
  { key: "guest", label: "Past guests" },
  { key: "contractor", label: "Contractors" },
  { key: "supplier", label: "Suppliers" },
  { key: "owner", label: "Owners" },
  { key: "suppressed", label: "Suppressed" },
];

const TYPE_TONE = {
  guest: "bg-teal-50 text-teal-800 border-teal-200",
  contractor: "bg-amber-50 text-amber-800 border-amber-200",
  supplier: "bg-sky-50 text-sky-800 border-sky-200",
  owner: "bg-violet-50 text-violet-800 border-violet-200",
};

const EMPTY = {
  type: "guest",
  first_name: "",
  last_name: "",
  company: "",
  email: "",
  phone: "",
  address: "",
  trade: "",
  tags: [],
  notes: "",
  birthday: "",
};

export default function Contacts() {
  const sectionTheme = useSectionTheme("contacts");
  const nav = useNavigate();
  const [tab, setTab] = useState("all");
  const [channel, setChannel] = useState(null);
  const [segment, setSegment] = useState(null);
  const [preferenceTag, setPreferenceTag] = useState(null);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [tagsInput, setTagsInput] = useState("");
  const [recomputing, setRecomputing] = useState(false);

  const load = () => {
    const params = {};
    if (tab === "suppressed") {
      params.suppressed = true;
    } else if (tab !== "all") {
      params.type = tab;
    }
    if (channel) params.tag = channel;
    if (preferenceTag) params.tag = preferenceTag;
    if (segment) params.segment = segment;
    if (search.trim()) params.search = search.trim();
    api.get("/contacts", { params }).then((r) => setRows(r.data));
  };

  const recomputeSegments = async () => {
    setRecomputing(true);
    try {
      const { data } = await api.post("/contacts/recompute-segments");
      toast.success(`${data.with_segments}/${data.total} guests now tagged`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setRecomputing(false);
    }
  };

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const importType = tab === "all" || tab === "suppressed" ? "guest" : tab;
    if (!window.confirm(`Import as ${importType}s?\n\nCSV must have a header row. Recognised columns: email, first_name, last_name, phone, company, address, trade, tags, notes.`)) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/contacts/import?type=${importType}`, fd);
      toast.success(`${data.created} added · ${data.updated} updated`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const resubscribeContact = async (c) => {
    try {
      await api.post(`/contacts/${c.id}/resubscribe`);
      toast.success(`${c.first_name} re-subscribed`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  useEffect(() => { load(); }, [tab, search, channel, segment, preferenceTag]);

  const openNew = () => {
    setForm(EMPTY);
    setTagsInput("");
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        email: form.email || null,
        tags: tagsInput
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      };
      await api.post("/contacts", payload);
      toast.success("Contact added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const rate = async (contactId, rating, e) => {
    if (e) e.stopPropagation();
    try {
      await api.put(`/contacts/${contactId}/rating`, { rating });
      toast.success(rating ? `Rated ${rating}★` : "Rating cleared");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Layout
      title="Contacts"
      subtitle="Everyone you've hosted, worked with, or plan to. Filter by type or search across the whole roster."
      actions={
        <>
          <Button
            data-testid={CONTACTS.recomputeBtn}
            variant="outline"
            onClick={recomputeSegments}
            disabled={recomputing}
            className="rounded-full h-11 px-5"
            title="Recompute repeat / long-stay / high-value tags for every guest"
          >
            {recomputing ? "Tagging…" : "Refresh segments"}
          </Button>
          <label className="rounded-full h-11 px-5 border border-slate-200 bg-white cursor-pointer hover:border-teal-500 hover:text-teal-700 text-sm text-slate-700 inline-flex items-center gap-2">
            Import CSV
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={uploadCsv} />
          </label>
          <Button
            data-testid={CONTACTS.addBtn}
            onClick={openNew}
            className="rounded-full ns-btn-primary h-11 px-6"
          >
            <Plus className="w-4 h-4 mr-2" /> Add contact
          </Button>
        </>
      }
    >
      <div data-testid={CONTACTS.root} className="space-y-6">
        <section className={`ns-section ns-section-${sectionTheme}`} data-section-theme={sectionTheme}>
          <div className="flex items-start gap-3 mb-4">
            <div className={`ns-rail ns-rail-${sectionTheme} self-stretch min-h-[2.5rem]`} />
            <div className="flex-1 min-w-0">
              <div className="ns-label text-indigo-800">Filters</div>
              <div className="font-serif text-lg text-slate-900 mt-0.5">Narrow your roster</div>
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
          <div className="flex bg-white rounded-full border border-slate-200 p-1">
            {TYPES.map((t) => (
              <button
                key={t.key}
                data-testid={
                  t.key === "all"
                    ? CONTACTS.tabAll
                    : t.key === "guest"
                      ? CONTACTS.tabGuest
                      : t.key === "contractor"
                        ? CONTACTS.tabContractor
                        : t.key === "supplier"
                          ? CONTACTS.tabSupplier
                          : CONTACTS.tabOwner
                }
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 rounded-full text-sm ${
                  tab === t.key
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="relative flex-1 min-w-[260px] max-w-md">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid={CONTACTS.searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, phone, trade…"
              className="pl-11 h-11 bg-white rounded-full"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="ns-label mr-1">Channel:</span>
          {[
            { k: null, label: "All" },
            { k: "airbnbofficial", label: "Airbnb" },
            { k: "bookingcom", label: "Booking.com" },
            { k: "homeaway", label: "VRBO" },
            { k: "direct", label: "Direct" },
            { k: "hostaway", label: "All Hostaway" },
          ].map((c) => (
            <button
              key={c.label}
              onClick={() => setChannel(c.k)}
              className={`px-3 py-1.5 rounded-full text-xs border ${
                channel === c.k
                  ? "bg-teal-600 text-white border-teal-600"
                  : "bg-white text-slate-600 border-slate-200 hover:border-teal-400"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="ns-label mr-1">Segment:</span>
          {[
            { k: null, label: "Everyone" },
            { k: "repeat", label: "Repeat guests" },
            { k: "long-stay", label: "Long stays" },
            { k: "high-value", label: "High value" },
          ].map((s) => (
            <button
              key={s.label}
              data-testid={s.k ? CONTACTS.segmentChip(s.k) : CONTACTS.segmentChip("all")}
              onClick={() => {
                setSegment(s.k);
                setPreferenceTag(null);
              }}
              className={`px-3 py-1.5 rounded-full text-xs border ${
                segment === s.k && !preferenceTag
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="ns-label mr-1">Preference:</span>
          {[
            { k: "dog-owner", label: "Dog owners" },
            { k: "family", label: "Families" },
            { k: "corporate", label: "Corporate" },
            { k: "anniversary", label: "Anniversary" },
            { k: "honeymoon", label: "Honeymoon" },
            { k: "wedding", label: "Wedding" },
          ].map((p) => (
            <button
              key={p.k}
              data-testid={CONTACTS.segmentChip(p.k)}
              onClick={() => {
                setPreferenceTag(preferenceTag === p.k ? null : p.k);
                setSegment(null);
              }}
              className={`px-3 py-1.5 rounded-full text-xs border ${
                preferenceTag === p.k
                  ? "bg-teal-700 text-white border-teal-700"
                  : "bg-white text-slate-600 border-slate-200 hover:border-teal-400"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        </section>

        <div data-testid={CONTACTS.table} className="ns-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="text-left border-b border-slate-100">
                <th className="ns-label px-6 py-4">Name</th>
                <th className="ns-label px-6 py-4">Type</th>
                <th className="ns-label px-6 py-4">Email</th>
                <th className="ns-label px-6 py-4">Phone</th>
                <th className="ns-label px-6 py-4">Tags</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center text-slate-400">
                    No contacts yet. Add your first one to get started.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={CONTACTS.row(r.id)}
                  onClick={() => nav(`/contacts/${r.id}`)}
                  className="border-b border-slate-100 hover:bg-teal-50/30 cursor-pointer"
                >
                  <td className="px-6 py-4">
                    <div className="font-medium text-slate-900">
                      {r.first_name} {r.last_name}
                    </div>
                    {r.company && (
                      <div className="text-xs text-slate-500">{r.company}</div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border ${TYPE_TONE[r.type]}`}
                    >
                      {r.type}
                      {r.trade && <span className="text-[10px] opacity-70">· {r.trade}</span>}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <span>{r.email || "—"}</span>
                      {r.email_status && r.email_status !== "active" && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-100 capitalize"
                          title={r.email_status_reason || ""}
                        >
                          {r.email_status}
                        </span>
                      )}
                      {tab === "suppressed" && r.email_status && r.email_status !== "active" && (
                        <button
                          onClick={(e) => { e.stopPropagation(); resubscribeContact(r); }}
                          className="text-[10px] text-teal-700 hover:underline ml-1"
                          title="Re-subscribe this contact"
                        >
                          Re-subscribe
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">{r.phone || "—"}</td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {r.type === "contractor" && (
                        <div
                          className="inline-flex items-center gap-0.5 mr-1"
                          data-testid={`contacts-rating-${r.id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {[1, 2, 3, 4, 5].map((n) => (
                            <button
                              key={n}
                              onClick={(e) => rate(r.id, r.rating === n ? null : n, e)}
                              className="p-0.5"
                              title={r.rating === n ? "Click to clear" : `Rate ${n} stars`}
                            >
                              <Star
                                className={`w-3.5 h-3.5 ${
                                  (r.rating || 0) >= n
                                    ? "fill-amber-400 text-amber-400"
                                    : "text-slate-200 hover:text-amber-300"
                                }`}
                              />
                            </button>
                          ))}
                        </div>
                      )}
                      {(r.segments || []).map((s) => (
                        <Badge
                          key={`seg-${s}`}
                          variant="secondary"
                          className={`rounded-full font-normal ${
                            s === "repeat"
                              ? "bg-teal-50 text-teal-800 hover:bg-teal-100"
                              : s === "long-stay"
                                ? "bg-amber-50 text-amber-800 hover:bg-amber-100"
                                : "bg-violet-50 text-violet-800 hover:bg-violet-100"
                          }`}
                        >
                          {s}
                        </Badge>
                      ))}
                      {(r.tags || []).slice(0, 3).map((t) => (
                        <Badge
                          key={t}
                          variant="secondary"
                          className="bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-full font-normal"
                        >
                          {t}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-slate-300">
                    <ChevronRight className="w-4 h-4" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={CONTACTS.dialog} className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">New contact</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 mt-2">
            <div className="col-span-2">
              <Label className="ns-label">Contact type</Label>
              <Select
                value={form.type}
                onValueChange={(v) => setForm({ ...form, type: v })}
              >
                <SelectTrigger data-testid={CONTACTS.fieldType} className="mt-2 h-11 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="guest">Past guest</SelectItem>
                  <SelectItem value="contractor">Contractor</SelectItem>
                  <SelectItem value="supplier">Supplier / Company</SelectItem>
                  <SelectItem value="owner">Owner / Partner</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="ns-label">First name</Label>
              <Input
                data-testid={CONTACTS.fieldFirstName}
                className="mt-2 h-11 bg-white"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              />
            </div>
            <div>
              <Label className="ns-label">Last name</Label>
              <Input
                data-testid={CONTACTS.fieldLastName}
                className="mt-2 h-11 bg-white"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              />
            </div>

            <div>
              <Label className="ns-label">Company</Label>
              <Input
                data-testid={CONTACTS.fieldCompany}
                className="mt-2 h-11 bg-white"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
              />
            </div>
            {form.type === "contractor" && (
              <div>
                <Label className="ns-label">Trade</Label>
                <Input
                  data-testid={CONTACTS.fieldTrade}
                  className="mt-2 h-11 bg-white"
                  placeholder="Plumber, cleaner, electrician…"
                  value={form.trade}
                  onChange={(e) => setForm({ ...form, trade: e.target.value })}
                />
              </div>
            )}

            <div>
              <Label className="ns-label">Email</Label>
              <Input
                data-testid={CONTACTS.fieldEmail}
                type="email"
                className="mt-2 h-11 bg-white"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <Label className="ns-label">Phone</Label>
              <Input
                data-testid={CONTACTS.fieldPhone}
                className="mt-2 h-11 bg-white"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>

            <div className="col-span-2">
              <Label className="ns-label">Birthday (optional)</Label>
              <Input
                data-testid={CONTACTS.fieldBirthday}
                type="date"
                className="mt-2 h-11 bg-white"
                value={form.birthday || ""}
                onChange={(e) => setForm({ ...form, birthday: e.target.value })}
              />
              <div className="text-[11px] text-slate-400 mt-1.5">
                Newport Stays will send this guest a warm birthday greeting each year on this date.
              </div>
            </div>

            <div className="col-span-2">
              <Label className="ns-label">Address</Label>
              <Input
                data-testid={CONTACTS.fieldAddress}
                className="mt-2 h-11 bg-white"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </div>

            <div className="col-span-2">
              <Label className="ns-label">Tags (comma separated)</Label>
              <Input
                data-testid={CONTACTS.fieldTags}
                className="mt-2 h-11 bg-white"
                placeholder="VIP, repeat guest, plumbing…"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
              />
            </div>

            <div className="col-span-2">
              <Label className="ns-label">Notes</Label>
              <Textarea
                data-testid={CONTACTS.fieldNotes}
                className="mt-2 bg-white"
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter className="mt-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid={CONTACTS.saveBtn}
              onClick={save}
              className="rounded-full ns-btn-primary px-6"
            >
              Save contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
